import React, { useState } from 'react';
import { useCRM } from '../context/CRMContext';
import { 
  Plus, 
  Search, 
  Filter, 
  Edit3, 
  Trash2, 
  Mail, 
  Phone, 
  Building,
  DollarSign,
  Calendar,
  Target,
  Star
} from 'lucide-react';

const Investors = () => {
  const { investors, addInvestor, updateInvestor, deleteInvestor } = useCRM();
  const [showModal, setShowModal] = useState(false);
  const [editingInvestor, setEditingInvestor] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStage, setFilterStage] = useState('all');
  const [filterType, setFilterType] = useState('all');

  const [formData, setFormData] = useState({
    name: '',
    firm: '',
    email: '',
    phone: '',
    investorType: 'angel',
    focusStage: 'seed',
    focusIndustries: '',
    ticketSize: '',
    status: 'researching',
    notes: '',
    linkedinUrl: '',
    lastContact: ''
  });

  const investorTypes = [
    { value: 'angel', label: 'Angel Investor', description: 'Individual investors' },
    { value: 'vc', label: 'Venture Capital', description: 'VC firms and funds' },
    { value: 'accelerator', label: 'Accelerator', description: 'Startup accelerators' },
    { value: 'corporate', label: 'Corporate VC', description: 'Corporate venture arms' },
    { value: 'government', label: 'Government', description: 'Government grants/funding' },
    { value: 'crowdfunding', label: 'Crowdfunding', description: 'Platform-based funding' }
  ];

  const focusStages = [
    { value: 'pre-seed', label: 'Pre-Seed', description: '$10K - $100K' },
    { value: 'seed', label: 'Seed', description: '$100K - $2M' },
    { value: 'series-a', label: 'Series A', description: '$2M - $15M' },
    { value: 'series-b', label: 'Series B+', description: '$15M+' }
  ];

  const statuses = [
    { value: 'researching', label: 'Researching', color: 'bg-gray-100 text-gray-800' },
    { value: 'contacted', label: 'Contacted', color: 'bg-blue-100 text-blue-800' },
    { value: 'interested', label: 'Interested', color: 'bg-green-100 text-green-800' },
    { value: 'meeting_scheduled', label: 'Meeting Scheduled', color: 'bg-purple-100 text-purple-800' },
    { value: 'pitch_sent', label: 'Pitch Sent', color: 'bg-yellow-100 text-yellow-800' },
    { value: 'due_diligence', label: 'Due Diligence', color: 'bg-indigo-100 text-indigo-800' },
    { value: 'term_sheet', label: 'Term Sheet', color: 'bg-green-100 text-green-800' },
    { value: 'closed', label: 'Invested', color: 'bg-green-200 text-green-900' },
    { value: 'passed', label: 'Passed', color: 'bg-red-100 text-red-800' }
  ];

  const resetForm = () => {
    setFormData({
      name: '',
      firm: '',
      email: '',
      phone: '',
      investorType: 'angel',
      focusStage: 'seed',
      focusIndustries: '',
      ticketSize: '',
      status: 'researching',
      notes: '',
      linkedinUrl: '',
      lastContact: ''
    });
    setEditingInvestor(null);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingInvestor) {
      updateInvestor(editingInvestor.id, formData);
    } else {
      addInvestor(formData);
    }
    setShowModal(false);
    resetForm();
  };

  const handleEdit = (investor) => {
    setFormData(investor);
    setEditingInvestor(investor);
    setShowModal(true);
  };

  const filteredInvestors = investors.filter(investor => {
    const matchesSearch = investor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         investor.firm.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStage = filterStage === 'all' || investor.focusStage === filterStage;
    const matchesType = filterType === 'all' || investor.investorType === filterType;
    return matchesSearch && matchesStage && matchesType;
  });

  const getStatusInfo = (status) => statuses.find(s => s.value === status) || statuses[0];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Investor Relations</h1>
          <p className="text-gray-600 mt-2">Manage your fundraising pipeline and investor relationships</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="btn-primary flex items-center space-x-2"
        >
          <Plus size={20} />
          <span>Add Investor</span>
        </button>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Search investors or firms..."
              className="input-field pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            value={filterStage}
            onChange={(e) => setFilterStage(e.target.value)}
            className="input-field"
          >
            <option value="all">All Stages</option>
            {focusStages.map(stage => (
              <option key={stage.value} value={stage.value}>{stage.label}</option>
            ))}
          </select>
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="input-field"
          >
            <option value="all">All Types</option>
            {investorTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Investors Grid */}
      {filteredInvestors.length === 0 ? (
        <div className="card text-center py-12">
          <Building className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">No investors found</h3>
          <p className="mt-1 text-sm text-gray-500">
            {investors.length === 0 ? "Get started by adding your first investor." : "Try adjusting your search or filters."}
          </p>
          <div className="mt-6">
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="btn-primary"
            >
              <Plus className="mr-2 h-4 w-4" />
              Add Investor
            </button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {filteredInvestors.map((investor) => {
            const statusInfo = getStatusInfo(investor.status);
            const typeInfo = investorTypes.find(t => t.value === investor.investorType);
            const stageInfo = focusStages.find(s => s.value === investor.focusStage);
            
            return (
              <div key={investor.id} className="card hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900">{investor.name}</h3>
                    <p className="text-gray-600 flex items-center">
                      <Building className="mr-1 h-4 w-4" />
                      {investor.firm || 'Independent'}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(investor)}
                      className="p-1 text-gray-400 hover:text-gray-600"
                    >
                      <Edit3 size={16} />
                    </button>
                    <button
                      onClick={() => deleteInvestor(investor.id)}
                      className="p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                      {statusInfo.label}
                    </span>
                    <span className="text-sm text-gray-500">{typeInfo?.label}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center text-gray-600">
                      <Target className="mr-2 h-4 w-4" />
                      {stageInfo?.label}
                    </div>
                    {investor.ticketSize && (
                      <div className="flex items-center text-gray-600">
                        <DollarSign className="mr-2 h-4 w-4" />
                        {investor.ticketSize}
                      </div>
                    )}
                  </div>

                  {investor.focusIndustries && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Focus Industries:</p>
                      <p className="text-sm text-gray-700">{investor.focusIndustries}</p>
                    </div>
                  )}

                  <div className="flex space-x-2 pt-3 border-t border-gray-100">
                    {investor.email && (
                      <a
                        href={`mailto:${investor.email}`}
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Mail size={16} />
                      </a>
                    )}
                    {investor.phone && (
                      <a
                        href={`tel:${investor.phone}`}
                        className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded"
                      >
                        <Phone size={16} />
                      </a>
                    )}
                    {investor.linkedinUrl && (
                      <a
                        href={investor.linkedinUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Building size={16} />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-semibold mb-6">
                {editingInvestor ? 'Edit Investor' : 'Add New Investor'}
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Investor Name *"
                    className="input-field"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Firm/Company"
                    className="input-field"
                    value={formData.firm}
                    onChange={(e) => setFormData({...formData, firm: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="email"
                    placeholder="Email"
                    className="input-field"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                  />
                  <input
                    type="tel"
                    placeholder="Phone"
                    className="input-field"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    className="input-field"
                    value={formData.investorType}
                    onChange={(e) => setFormData({...formData, investorType: e.target.value})}
                  >
                    {investorTypes.map(type => (
                      <option key={type.value} value={type.value}>{type.label}</option>
                    ))}
                  </select>
                  <select
                    className="input-field"
                    value={formData.focusStage}
                    onChange={(e) => setFormData({...formData, focusStage: e.target.value})}
                  >
                    {focusStages.map(stage => (
                      <option key={stage.value} value={stage.value}>{stage.label}</option>
                    ))}
                  </select>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input
                    type="text"
                    placeholder="Focus Industries (e.g., SaaS, Fintech)"
                    className="input-field"
                    value={formData.focusIndustries}
                    onChange={(e) => setFormData({...formData, focusIndustries: e.target.value})}
                  />
                  <input
                    type="text"
                    placeholder="Typical Ticket Size (e.g., $50K-$500K)"
                    className="input-field"
                    value={formData.ticketSize}
                    onChange={(e) => setFormData({...formData, ticketSize: e.target.value})}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <select
                    className="input-field"
                    value={formData.status}
                    onChange={(e) => setFormData({...formData, status: e.target.value})}
                  >
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                  <input
                    type="url"
                    placeholder="LinkedIn URL"
                    className="input-field"
                    value={formData.linkedinUrl}
                    onChange={(e) => setFormData({...formData, linkedinUrl: e.target.value})}
                  />
                </div>

                <textarea
                  placeholder="Notes (investment thesis, connection, meeting notes...)"
                  className="input-field h-24 resize-none"
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                />

                <div className="flex justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowModal(false);
                      resetForm();
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary">
                    {editingInvestor ? 'Update' : 'Add'} Investor
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Investors;